import { isPlatformBrowser } from '@angular/common';
import { FlashMapNodeModel } from '../models/node.model';
import {
  Direction,
  EventData,
  FlashMapLayoutMode,
  FlashMapNodeLineStyle,
  FlashMapOptions,
  MindMapData,
  NodeImageMeta,
} from '../models/types';
import { MindMapEngine } from './mind-map-engine';

const SCREENSHOT_PADDING_PX = 32;
const SCREENSHOT_TARGET_SCALE = 3;
const SCREENSHOT_MAX_CANVAS_PIXELS = 64_000_000;
const SCREENSHOT_MAX_CANVAS_DIMENSION = 16_384;

type Html2Canvas = (
  element: HTMLElement,
  options?: {
    backgroundColor?: string | null;
    scale?: number;
    useCORS?: boolean;
    logging?: boolean;
    width?: number;
    height?: number;
    windowWidth?: number;
    windowHeight?: number;
  },
) => Promise<HTMLCanvasElement>;

type RenderedMapBounds = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

type ScreenshotState = {
  zoom: number;
  pan: { x: number; y: number };
  viewSize: { w: number; h: number } | null;
  graphDpr: number | null;
  screenshotSize: { width: number; height: number; scale: number } | null;
  panelStyle: {
    width: string;
    height: string;
    overflow: string;
  };
  nodesStyle: {
    width: string;
    height: string;
  } | null;
};

export class JsMindAdapter implements MindMapEngine {
  private jsmindInstance: any = null;
  private assetBasePath = '/assets/flash-map';
  private screenshotOptions: { filename?: string | null; background?: string } = {};

  constructor(private readonly platformId: object) {}

  async init(options: FlashMapOptions): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('JsMindAdapter can only be initialized in browser environment');
      return;
    }

    this.assetBasePath = options.assetBasePath ?? '/assets/flash-map';
    this.screenshotOptions = options.plugin?.['screenshot'] ?? {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const jsMindModule: any = await this.importModuleOrAsset(
      () => this.dynamicImport('../../core/jsmind.js'),
      [`${this.assetBasePath}/jsmind.js`],
    );
    const jsMind = jsMindModule.default ?? jsMindModule;

    // Load draggable-node plugin for drag & drop support
    try {
      await this.importModuleOrAsset(
        () => this.dynamicImport('../../core/plugins/jsmind.draggable-node.js'),
        [`${this.assetBasePath}/plugins/jsmind.draggable-node.js`],
      );
    } catch (pluginError) {
      console.warn('Failed to load draggable-node plugin:', pluginError);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.jsmindInstance = new jsMind(options as any);
  }

  isReady(): boolean {
    return this.jsmindInstance !== null;
  }

  destroy(): void {
    this.clearEventListener();
    this.jsmindInstance = null;
  }

  addEventListener(callback: (type: number, data: EventData) => void): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.add_event_listener(callback);
  }

  clearEventListener(): void {
    if (this.jsmindInstance) {
      this.jsmindInstance.clear_event_listener();
    }
  }

  show(mindData: MindMapData | null, skipCentering = false): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.show(mindData, skipCentering);
  }

  setLayoutMode(layoutMode: FlashMapLayoutMode): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.set_layout_mode(layoutMode);
  }

  getMeta(): { name: string; author: string; version: string } {
    if (!this.jsmindInstance) {
      return { name: '', author: '', version: '' };
    }
    return this.jsmindInstance.get_meta();
  }

  getData(format: 'node_tree' | 'node_array' | 'freemind' = 'node_tree'): object {
    if (!this.jsmindInstance) {
      return {};
    }
    return this.jsmindInstance.get_data(format);
  }

  shootScreenshot(): void {
    if (!this.jsmindInstance) {
      return;
    }
    void this.shootWithHtml2Canvas();
  }

  private async shootWithHtml2Canvas(): Promise<void> {
    if (!this.jsmindInstance) {
      return;
    }
    const screenshotState = this.prepareFullMapScreenshot();
    let screenshotSurface: HTMLElement | null = null;

    try {
      const container = this.getContainer();
      if (!container) {
        console.warn('Screenshot fallback unavailable: container not found.');
        return;
      }

      const html2canvas = await this.ensureHtml2Canvas();
      if (!html2canvas) {
        console.warn('html2canvas not available.');
        return;
      }

      const background = this.screenshotOptions.background;
      const screenshotWidth =
        screenshotState?.screenshotSize?.width ||
        container.offsetWidth ||
        this.readPixelValue(container.style.width);
      const screenshotHeight =
        screenshotState?.screenshotSize?.height ||
        container.offsetHeight ||
        this.readPixelValue(container.style.height);
      const screenshotScale =
        screenshotState?.screenshotSize?.scale ||
        this.resolveScreenshotScale(screenshotWidth, screenshotHeight);
      screenshotSurface = this.createScreenshotSurface(
        container,
        screenshotWidth,
        screenshotHeight,
      );
      const canvas = await html2canvas(screenshotSurface, {
        backgroundColor: background && background !== 'transparent' ? background : null,
        scale: screenshotScale,
        useCORS: true,
        logging: false,
        width: screenshotWidth,
        height: screenshotHeight,
        windowWidth: screenshotWidth,
        windowHeight: screenshotHeight,
      });
      const nameBase = this.resolveScreenshotFilenameBase();
      this.triggerDownload(`${nameBase}.png`, canvas.toDataURL('image/png'));
    } catch (error) {
      console.warn('html2canvas failed:', error);
    } finally {
      screenshotSurface?.remove();
      this.restoreScreenshotState(screenshotState);
    }
  }

  /**
   * Captures a detached copy of the expanded map instead of the live panel.
   *
   * The live panel sits below several clipped editor layers. Rendering a clone attached directly to
   * the document keeps the calculated node/graph translation while preventing ancestor overflow or
   * the user's current viewport from cutting the PNG. Canvas pixels are copied explicitly because
   * cloneNode does not preserve a canvas drawing buffer.
   */
  private createScreenshotSurface(
    container: HTMLElement,
    width: number,
    height: number,
  ): HTMLElement {
    const surface = container.cloneNode(true) as HTMLElement;
    surface.setAttribute('data-flash-map-screenshot-surface', 'true');
    surface.style.position = 'fixed';
    surface.style.left = '0';
    surface.style.top = '0';
    surface.style.width = `${width}px`;
    surface.style.height = `${height}px`;
    surface.style.overflow = 'visible';
    surface.style.transform = 'none';
    surface.style.zoom = '1';
    surface.style.pointerEvents = 'none';
    surface.style.zIndex = '-1';

    // The clone is mounted outside .flash-map-root, so retain any inherited theme variables.
    const computedStyle = getComputedStyle(container);
    for (let index = 0; index < computedStyle.length; index += 1) {
      const property = computedStyle.item(index);
      if (property.startsWith('--')) {
        surface.style.setProperty(property, computedStyle.getPropertyValue(property));
      }
    }

    const sourceCanvases = container.querySelectorAll<HTMLCanvasElement>('canvas');
    const clonedCanvases = surface.querySelectorAll<HTMLCanvasElement>('canvas');
    sourceCanvases.forEach((source, index) => {
      const clone = clonedCanvases[index];
      if (!clone) {
        return;
      }
      clone.width = source.width;
      clone.height = source.height;
      const context = clone.getContext('2d');
      context?.drawImage(source, 0, 0);
    });

    document.body.appendChild(surface);
    return surface;
  }

  private prepareFullMapScreenshot(): ScreenshotState | null {
    const view = this.jsmindInstance?.view;
    const panel = this.getContainer();
    if (!view || !panel) {
      return null;
    }

    const state: ScreenshotState = {
      zoom: this.getZoom(),
      pan: view.pan ? { x: view.pan.x, y: view.pan.y } : { x: 0, y: 0 },
      viewSize: view.size ? { w: view.size.w, h: view.size.h } : null,
      graphDpr: typeof view.graph?.dpr === 'number' ? view.graph.dpr : null,
      screenshotSize: null,
      panelStyle: {
        width: panel.style.width,
        height: panel.style.height,
        overflow: panel.style.overflow,
      },
      nodesStyle: view.e_nodes
        ? {
            width: view.e_nodes.style.width,
            height: view.e_nodes.style.height,
          }
        : null,
    };

    if (state.zoom !== 1 && typeof view.set_zoom === 'function') {
      view.set_zoom(1);
    }
    if (typeof view.set_pan === 'function') {
      view.set_pan(0, 0);
    }
    if (typeof view.relayout === 'function') {
      view.relayout();
    }

    const bounds = this.getRenderedMapBounds();
    if (!bounds) {
      return state;
    }

    const width = Math.ceil(bounds.width + SCREENSHOT_PADDING_PX * 2);
    const height = Math.ceil(bounds.height + SCREENSHOT_PADDING_PX * 2);
    const panX = Math.ceil(SCREENSHOT_PADDING_PX - bounds.left);
    const panY = Math.ceil(SCREENSHOT_PADDING_PX - bounds.top);
    const scale = this.resolveScreenshotScale(width, height);
    state.screenshotSize = { width, height, scale };

    panel.style.width = `${width}px`;
    panel.style.height = `${height}px`;
    panel.style.overflow = 'visible';

    if (view.size) {
      view.size.w = width;
      view.size.h = height;
    }
    if (typeof view.graph?.dpr === 'number') {
      view.graph.dpr = scale;
    }
    view.graph?.set_size?.(width, height);
    if (view.e_nodes) {
      view.e_nodes.style.width = `${width}px`;
      view.e_nodes.style.height = `${height}px`;
    }
    if (typeof view.set_pan === 'function') {
      view.set_pan(panX, panY);
    }
    if (typeof view.show_nodes === 'function') {
      view.show_nodes();
    }
    if (typeof view.show_lines === 'function') {
      view.show_lines();
    }

    return state;
  }

  private restoreScreenshotState(state: ScreenshotState | null): void {
    const view = this.jsmindInstance?.view;
    const panel = this.getContainer();
    if (!state || !view || !panel) {
      return;
    }

    panel.style.width = state.panelStyle.width;
    panel.style.height = state.panelStyle.height;
    panel.style.overflow = state.panelStyle.overflow;

    if (state.viewSize && view.size) {
      view.size.w = state.viewSize.w;
      view.size.h = state.viewSize.h;
    }
    if (state.graphDpr !== null && typeof view.graph?.dpr === 'number') {
      view.graph.dpr = state.graphDpr;
    }
    view.graph?.set_size?.(state.viewSize?.w ?? 1, state.viewSize?.h ?? 1);
    if (state.nodesStyle && view.e_nodes) {
      view.e_nodes.style.width = state.nodesStyle.width;
      view.e_nodes.style.height = state.nodesStyle.height;
    }
    if (state.zoom !== this.getZoom() && typeof view.set_zoom === 'function') {
      view.set_zoom(state.zoom);
    }
    if (typeof view.set_pan === 'function') {
      view.set_pan(state.pan.x, state.pan.y);
    }
    if (typeof view.relayout === 'function') {
      view.relayout();
    }
  }

  private getRenderedMapBounds(): RenderedMapBounds | null {
    const nodesContainer = this.jsmindInstance?.view?.e_nodes as HTMLElement | null | undefined;
    if (!nodesContainer) {
      return null;
    }

    const elements = nodesContainer.querySelectorAll<HTMLElement>('jmnode, jmexpander');
    let left = Number.POSITIVE_INFINITY;
    let top = Number.POSITIVE_INFINITY;
    let right = Number.NEGATIVE_INFINITY;
    let bottom = Number.NEGATIVE_INFINITY;

    elements.forEach((element) => {
      if (element.style.display === 'none' || getComputedStyle(element).display === 'none') {
        return;
      }

      const elementLeft = this.readPixelValue(element.style.left);
      const elementTop = this.readPixelValue(element.style.top);
      const elementWidth = this.readElementWidth(element);
      const elementHeight = this.readElementHeight(element);

      if (
        !Number.isFinite(elementLeft) ||
        !Number.isFinite(elementTop) ||
        !Number.isFinite(elementWidth) ||
        !Number.isFinite(elementHeight)
      ) {
        return;
      }

      left = Math.min(left, elementLeft);
      top = Math.min(top, elementTop);
      right = Math.max(right, elementLeft + elementWidth);
      bottom = Math.max(bottom, elementTop + elementHeight);
    });

    if (
      !Number.isFinite(left) ||
      !Number.isFinite(top) ||
      !Number.isFinite(right) ||
      !Number.isFinite(bottom)
    ) {
      return null;
    }

    return {
      left,
      top,
      right,
      bottom,
      width: right - left,
      height: bottom - top,
    };
  }

  private resolveScreenshotScale(width: number, height: number): number {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return SCREENSHOT_TARGET_SCALE;
    }

    const maxScaleByWidth = SCREENSHOT_MAX_CANVAS_DIMENSION / width;
    const maxScaleByHeight = SCREENSHOT_MAX_CANVAS_DIMENSION / height;
    const maxScaleByPixels = Math.sqrt(SCREENSHOT_MAX_CANVAS_PIXELS / (width * height));
    const safeScale = Math.min(
      SCREENSHOT_TARGET_SCALE,
      maxScaleByWidth,
      maxScaleByHeight,
      maxScaleByPixels,
    );

    // A map that exceeds even the 1x canvas limit must still export in one piece. In that case,
    // prefer a smaller bitmap density over clipping the content or failing the download.
    return Math.min(SCREENSHOT_TARGET_SCALE, safeScale);
  }

  private readElementWidth(element: HTMLElement): number {
    return element.offsetWidth || element.clientWidth || this.readPixelValue(element.style.width);
  }

  private readElementHeight(element: HTMLElement): number {
    return (
      element.offsetHeight || element.clientHeight || this.readPixelValue(element.style.height)
    );
  }

  private readPixelValue(value: string): number {
    const numericValue = Number.parseFloat(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private resolveScreenshotFilenameBase(): string {
    const candidates = [
      this.getRoot()?.topic,
      this.screenshotOptions.filename,
      this.getMeta().name,
      'mindmap',
    ];

    for (const candidate of candidates) {
      const sanitized = this.sanitizeFilenameBase(candidate);
      if (sanitized) {
        return sanitized;
      }
    }

    return 'mindmap';
  }

  private sanitizeFilenameBase(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value
      .replace(/[\\/:*?"<>|\u0000-\u001f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120)
      .replace(/[. ]+$/g, '');
  }

  getRoot(): FlashMapNodeModel | null {
    if (!this.jsmindInstance) {
      return null;
    }
    try {
      return this.jsmindInstance.get_root() as unknown as FlashMapNodeModel;
    } catch {
      return null;
    }
  }

  getNode(nodeId: string): FlashMapNodeModel | null {
    if (!this.jsmindInstance) {
      return null;
    }
    return this.jsmindInstance.get_node(nodeId) as unknown as FlashMapNodeModel;
  }

  addNode(
    parentNode: string | FlashMapNodeModel,
    nodeId: string,
    topic: string,
    data?: Record<string, unknown>,
    direction?: Direction | string | number,
  ): FlashMapNodeModel | null {
    if (!this.jsmindInstance) {
      return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.jsmindInstance.add_node(
      parentNode as any,
      nodeId,
      topic,
      data,
      direction as any,
    ) as unknown as FlashMapNodeModel;
  }

  addNodes(
    parentNode: string | FlashMapNodeModel,
    nodesData: Array<{
      id: string;
      topic: string;
      data?: Record<string, unknown>;
      direction?: Direction | string | number;
      children?: Array<unknown>;
    }>,
  ): Array<FlashMapNodeModel | null> {
    if (!this.jsmindInstance) {
      return [];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.jsmindInstance.add_nodes(
      parentNode as any,
      nodesData as any,
    ) as unknown as Array<FlashMapNodeModel | null>;
  }

  removeNode(node: string | FlashMapNodeModel): boolean {
    if (!this.jsmindInstance) {
      return false;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.jsmindInstance.remove_node(node as any);
  }

  moveNode(
    nodeId: string,
    beforeId: string | null,
    parentId: string,
    direction?: Direction | string | number,
  ): FlashMapNodeModel | null {
    if (!this.jsmindInstance) {
      return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.jsmindInstance.move_node(
      nodeId,
      beforeId,
      parentId,
      direction as any,
    ) as unknown as FlashMapNodeModel;
  }

  updateNode(nodeId: string, topic: string): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.update_node(nodeId, topic);
  }

  refreshNode(nodeId: string): void {
    if (!this.jsmindInstance || !nodeId) {
      return;
    }
    const node = this.jsmindInstance.get_node(nodeId);
    if (!node) {
      return;
    }
    if (typeof this.jsmindInstance._refresh_node_view === 'function') {
      this.jsmindInstance._refresh_node_view(node);
      return;
    }
    this.jsmindInstance.view?.update_node?.(node);
    this.jsmindInstance.layout?.layout?.();
    this.jsmindInstance.view?.relayout?.();
  }

  selectNode(node: string | FlashMapNodeModel): void {
    if (!this.jsmindInstance) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.jsmindInstance.select_node(node as any);
  }

  getSelectedNode(): FlashMapNodeModel | null {
    if (!this.jsmindInstance) {
      return null;
    }
    return this.jsmindInstance.get_selected_node() as unknown as FlashMapNodeModel;
  }

  selectClear(): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.select_clear();
  }

  expandNode(node: string | FlashMapNodeModel): void {
    if (!this.jsmindInstance) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.jsmindInstance.expand_node(node as any);
  }

  collapseNode(node: string | FlashMapNodeModel): void {
    if (!this.jsmindInstance) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.jsmindInstance.collapse_node(node as any);
  }

  toggleNode(node: string | FlashMapNodeModel): void {
    if (!this.jsmindInstance) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.jsmindInstance.toggle_node(node as any);
  }

  expandAll(): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.expand_all();
  }

  collapseAll(): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.collapse_all();
  }

  expandToDepth(depth: number): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.expand_to_depth(depth);
  }

  setNodeColor(nodeId: string, bgColor?: string, fgColor?: string): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.set_node_color(nodeId, bgColor, fgColor);
  }

  setNodeStyleColor(
    nodeId: string,
    key: 'background-color' | 'foreground-color' | 'border-color',
    color?: string | null,
  ): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.set_node_style_color(nodeId, key, color ?? null);
  }

  setNodeImage(nodeId: string, image: NodeImageMeta | null): void {
    const node = this.jsmindInstance?.get_node(nodeId);
    if (!node || !this.jsmindInstance?.get_editable?.()) return;
    if (!node.data || typeof node.data !== 'object') node.data = {};
    if (image) node.data.image = image;
    else delete node.data.image;
    this.refreshNode(nodeId);
    this.jsmindInstance.invoke_event_handle?.(3, {
      evt: 'set_node_image',
      data: [nodeId, image],
      node: nodeId,
    });
  }

  setNodeImagePosition(nodeId: string, position: 'top' | 'bottom' | 'left' | 'right'): void {
    const node = this.jsmindInstance?.get_node(nodeId);
    const image = node?.data?.image;
    if (!node || !image || typeof image !== 'object') return;
    this.setNodeImage(nodeId, { ...image, position });
  }

  setNodeDescendantLineColor(nodeId: string, color: string): void {
    if (!this.jsmindInstance || typeof color !== 'string' || color.trim().length === 0) {
      return;
    }
    this.jsmindInstance.set_node_descendant_line_color(nodeId, color);
  }

  resetNodeDescendantLineColorsToTheme(nodeId: string): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.reset_node_descendant_line_colors_to_theme(nodeId);
  }

  setNodeDescendantLineStyle(nodeId: string, style: FlashMapNodeLineStyle): void {
    if (!this.jsmindInstance || !style || typeof style !== 'object') {
      return;
    }
    this.jsmindInstance.set_node_descendant_line_style(nodeId, style);
  }

  setNodeStyleValue(
    nodeId: string,
    key: 'border-width' | 'border-style' | 'font-family',
    value?: string | null,
  ): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.set_node_style_value(nodeId, key, value ?? null);
  }

  setNodeShape(nodeId: string, shape?: string | null): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.set_node_shape(nodeId, shape ?? null);
  }

  resetNodeShapeToTheme(nodeId: string): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.reset_node_shape_to_theme(nodeId);
  }

  setNodeLineShape(nodeId: string, shape?: 'underline' | null): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.set_node_line_shape(nodeId, shape ?? null);
  }

  setNodeFontStyle(
    nodeId: string,
    size?: number | null,
    weight?: string | null,
    style?: string | null,
  ): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.set_node_font_style(nodeId, size, weight, style);
  }

  clearNodeCustomStyles(): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.clear_custom_style();
  }

  setTheme(theme: string | null): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.set_theme(theme);
  }

  enableEdit(): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.enable_edit();
  }

  disableEdit(): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.disable_edit();
  }

  getEditable(): boolean {
    if (!this.jsmindInstance) {
      return false;
    }
    return this.jsmindInstance.get_editable();
  }

  beginEdit(node: string | FlashMapNodeModel): void {
    if (!this.jsmindInstance) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.jsmindInstance.begin_edit(node as any);
  }

  endEdit(): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.end_edit();
  }

  resize(): void {
    if (!this.jsmindInstance) {
      return;
    }
    this.jsmindInstance.resize();
  }

  zoomIn(): void {
    if (!this.jsmindInstance || !this.jsmindInstance.view) {
      return;
    }
    this.jsmindInstance.view.zoom_in();
  }

  zoomOut(): void {
    if (!this.jsmindInstance || !this.jsmindInstance.view) {
      return;
    }
    this.jsmindInstance.view.zoom_out();
  }

  resetZoom(): void {
    if (!this.jsmindInstance || !this.jsmindInstance.view) {
      return;
    }
    this.jsmindInstance.view.set_zoom(1);
  }

  setZoom(zoomLevel: number): void {
    if (!this.jsmindInstance || !this.jsmindInstance.view) {
      return;
    }
    this.jsmindInstance.view.set_zoom(zoomLevel);
  }

  getZoom(): number {
    if (!this.jsmindInstance || !this.jsmindInstance.view) {
      return 1;
    }
    return this.jsmindInstance.view.zoom_current ?? 1;
  }

  getPan(): { x: number; y: number } {
    const pan = this.jsmindInstance?.view?.pan;
    return {
      x: typeof pan?.x === 'number' ? pan.x : 0,
      y: typeof pan?.y === 'number' ? pan.y : 0,
    };
  }

  setPan(x: number, y: number): void {
    if (!this.jsmindInstance?.view || typeof this.jsmindInstance.view.set_pan !== 'function') {
      return;
    }
    this.jsmindInstance.view.set_pan(x, y);
  }

  getContainer(): HTMLElement | null {
    if (!this.jsmindInstance || !this.jsmindInstance.view) {
      return null;
    }
    return this.jsmindInstance.view.e_panel ?? null;
  }

  centerNode(node?: string | FlashMapNodeModel): void {
    if (!this.jsmindInstance || !this.jsmindInstance.view) {
      return;
    }
    const nodeToCenter = typeof node === 'string' ? this.getNode(node) : (node ?? this.getRoot());
    if (nodeToCenter) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.jsmindInstance.view.center_node(nodeToCenter as any);
    }
  }

  private async importModuleOrAsset(
    moduleImporter: () => Promise<unknown>,
    candidates: string[],
  ): Promise<unknown> {
    try {
      return await this.importFirst(candidates);
    } catch {
      return await moduleImporter();
    }
  }

  private async importFirst(candidates: string[]): Promise<unknown> {
    let lastError: unknown = null;
    for (const candidate of candidates) {
      try {
        return await this.dynamicImport(candidate);
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  }

  private async dynamicImport(path: string): Promise<unknown> {
    const importer = new Function('p', 'return import(p);') as (p: string) => Promise<unknown>;
    return importer(path);
  }

  private async ensureHtml2Canvas(): Promise<Html2Canvas | null> {
    const existing = (globalThis as typeof globalThis & { html2canvas?: unknown }).html2canvas;
    if (existing && typeof existing === 'function') {
      return existing as Html2Canvas;
    }
    try {
      const html2canvasModule = await import('html2canvas');
      const html2canvas = (html2canvasModule as { default?: unknown }).default ?? html2canvasModule;
      if (typeof html2canvas === 'function') {
        (globalThis as typeof globalThis & { html2canvas?: unknown }).html2canvas = html2canvas;
        return html2canvas as Html2Canvas;
      }
      return null;
    } catch (error) {
      console.warn('Failed to load html2canvas:', error);
      return null;
    }
  }

  private triggerDownload(filename: string, dataUrl: string): void {
    const anchor = document.createElement('a');
    if ('download' in anchor) {
      anchor.style.visibility = 'hidden';
      anchor.href = dataUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } else {
      window.location.href = dataUrl;
    }
  }
}
